import type { QueryRowType } from "@rocicorp/zero";
import {
  estimateUsageCostUsd,
  type ModelPricing,
} from "../../../shared/aiModelPricing";
import {
  getAIProvider,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderId,
} from "../../../shared/aiProviders";
import type { queries } from "../../../shared/queries";
import type { MonitoringAiUsageRow } from "./aiUsage";
import { recordTokens } from "./aiUsage";
import {
  addRecordToActivityBuckets,
  emptyActivityBuckets,
  type ActivityCostBucket,
  type AiUsageActivity,
} from "./aiUsageActivities";

export type MonitorAiProvider = QueryRowType<
  ReturnType<typeof queries.monitorAiProviders>
>;

export type AiProviderModelCostRow = {
  model: string;
  estimatedCostUsd: number | null;
  requestCount: number;
  byActivity: Record<AiUsageActivity, ActivityCostBucket>;
};

export type AiProviderMonitoringRow = {
  id: string;
  label: string;
  providerType: string;
  chatModel: string | null;
  fastModel: string | null;
  active: boolean;
  estimatedCostUsd: number | null;
  requestCount: number;
  limitAmount: number | null;
  modelRows: AiProviderModelCostRow[];
};

export function providerLabel(provider: MonitorAiProvider): string {
  const type = provider.provider
    ? getAIProvider(resolveAIProviderId(provider)).label
    : "AI provider";
  const model = provider.chatModel ?? provider.fastModel;
  return model ? `${type} • ${model}` : type;
}

export function providerModelIds(provider: MonitorAiProvider): Set<string> {
  const models = new Set<string>();
  if (provider.chatModel) models.add(provider.chatModel);
  if (provider.fastModel) models.add(provider.fastModel);
  for (const row of provider.modelPricing ?? []) {
    if (row.modelId) models.add(row.modelId);
  }
  return models;
}

export type ManagedProviderDescriptor = {
  label: string;
  chatModel: string;
  fastModel: string;
};

export function computeAiProviderMonitoringRows(
  providers: readonly MonitorAiProvider[],
  usageRecords: readonly MonitoringAiUsageRow[],
  pricing: ReadonlyMap<string, ModelPricing>,
  budgets: readonly {
    scopeKind: string;
    scopeId: string;
    limitAmount: number | null;
  }[] = [],
  managed: ManagedProviderDescriptor | null = null
): AiProviderMonitoringRow[] {
  const usageByProvider = new Map<
    string,
    {
      cost: number;
      hasCost: boolean;
      requests: number;
      byModel: Map<string, AiProviderModelCostRow>;
    }
  >();

  for (const record of usageRecords) {
    // Managed/env-default usage carries a null aiProviderId; bucket it under the
    // managed sentinel so it surfaces as the 3B-managed provider row.
    const providerId =
      record.aiProviderId ?? (managed ? MANAGED_AI_PROVIDER_ID : null);
    if (!providerId) continue;
    const bucket = usageByProvider.get(providerId) ?? {
      cost: 0,
      hasCost: false,
      requests: 0,
      byModel: new Map<string, AiProviderModelCostRow>(),
    };
    bucket.requests += record.requestCount ?? 0;
    const cost = estimateUsageCostUsd(
      record.model,
      recordTokens(record),
      pricing,
      record.aiProviderId
    );
    if (cost != null) {
      bucket.cost += cost;
      bucket.hasCost = true;
    }
    const modelRow = bucket.byModel.get(record.model) ?? {
      model: record.model,
      estimatedCostUsd: null,
      requestCount: 0,
      byActivity: emptyActivityBuckets(),
    };
    modelRow.requestCount += record.requestCount ?? 0;
    if (cost != null) {
      modelRow.estimatedCostUsd = (modelRow.estimatedCostUsd ?? 0) + cost;
    }
    addRecordToActivityBuckets(modelRow.byActivity, record, pricing);
    bucket.byModel.set(record.model, modelRow);
    usageByProvider.set(providerId, bucket);
  }

  const rows: AiProviderMonitoringRow[] = providers.map((provider) => {
    const usage = usageByProvider.get(provider.id);
    const providerBudget = budgets.find(
      (b) => b.scopeKind === "ai_provider" && b.scopeId === provider.id
    );
    return {
      id: provider.id,
      label: providerLabel(provider),
      providerType: provider.provider ?? "unknown",
      chatModel: provider.chatModel,
      fastModel: provider.fastModel,
      active: provider.active ?? false,
      estimatedCostUsd: usage?.hasCost ? usage.cost : null,
      requestCount: usage?.requests ?? 0,
      limitAmount: providerBudget?.limitAmount ?? null,
      modelRows: [...(usage?.byModel.values() ?? [])]
        .filter((row) => providerModelIds(provider).has(row.model))
        .sort(
          (a, b) =>
            (b.estimatedCostUsd ?? 0) - (a.estimatedCostUsd ?? 0) ||
            a.model.localeCompare(b.model)
        ),
    };
  });

  if (managed) {
    const usage = usageByProvider.get(MANAGED_AI_PROVIDER_ID);
    const managedBudget = budgets.find(
      (b) =>
        b.scopeKind === "ai_provider" && b.scopeId === MANAGED_AI_PROVIDER_ID
    );
    rows.unshift({
      id: MANAGED_AI_PROVIDER_ID,
      label: managed.label,
      providerType: MANAGED_AI_PROVIDER_ID,
      chatModel: managed.chatModel,
      fastModel: managed.fastModel,
      active: false,
      estimatedCostUsd: usage?.hasCost ? usage.cost : null,
      requestCount: usage?.requests ?? 0,
      limitAmount: managedBudget?.limitAmount ?? null,
      modelRows: [...(usage?.byModel.values() ?? [])].sort(
        (a, b) =>
          (b.estimatedCostUsd ?? 0) - (a.estimatedCostUsd ?? 0) ||
          a.model.localeCompare(b.model)
      ),
    });
  }

  return rows;
}

export function computeModelCostRows(
  records: readonly MonitoringAiUsageRow[],
  pricing: ReadonlyMap<string, ModelPricing>
): AiProviderModelCostRow[] {
  const byModel = new Map<string, AiProviderModelCostRow>();
  for (const record of records) {
    const cost = estimateUsageCostUsd(
      record.model,
      recordTokens(record),
      pricing,
      record.aiProviderId
    );
    const modelRow = byModel.get(record.model) ?? {
      model: record.model,
      estimatedCostUsd: null,
      requestCount: 0,
      byActivity: emptyActivityBuckets(),
    };
    modelRow.requestCount += record.requestCount ?? 0;
    if (cost != null) {
      modelRow.estimatedCostUsd = (modelRow.estimatedCostUsd ?? 0) + cost;
    }
    addRecordToActivityBuckets(modelRow.byActivity, record, pricing);
    byModel.set(record.model, modelRow);
  }
  return [...byModel.values()].sort(
    (a, b) =>
      (b.estimatedCostUsd ?? 0) - (a.estimatedCostUsd ?? 0) ||
      a.model.localeCompare(b.model)
  );
}

export type AiProviderAllocationRow = {
  id: string;
  label: string;
  spendUsd: number | null;
  limitAmount: number | null;
  kind: "total" | "space" | "personal" | "unattributed";
  budgetId?: string;
};

export function computeAiProviderAllocationRows(
  providerId: string | null,
  usageRecords: readonly MonitoringAiUsageRow[],
  pricing: ReadonlyMap<string, ModelPricing>,
  spaces: readonly { id: string; name: string }[],
  spaceBudgets: readonly {
    id: string;
    scopeId: string;
    limitAmount: number | null;
  }[],
  providerLimitUsd: number | null,
  totalSpendUsd: number | null
): AiProviderAllocationRow[] {
  const rows: AiProviderAllocationRow[] = [];
  if (providerLimitUsd != null) {
    rows.push({
      id: "total",
      label: "Total spend",
      spendUsd: totalSpendUsd,
      limitAmount: providerLimitUsd,
      kind: "total",
    });
  }

  const spendBySpace = new Map<string, number>();
  let personalSpend = 0;
  for (const record of usageRecords) {
    if (providerId != null) {
      const matches =
        providerId === MANAGED_AI_PROVIDER_ID
          ? record.aiProviderId == null
          : record.aiProviderId === providerId;
      if (!matches) continue;
    }
    const cost = estimateUsageCostUsd(
      record.model,
      recordTokens(record),
      pricing,
      record.aiProviderId
    );
    if (cost == null) continue;
    const space = record.workflow?.space;
    if (space?.mode === "personal") {
      personalSpend += cost;
      continue;
    }
    if (!space?.id) continue;
    spendBySpace.set(space.id, (spendBySpace.get(space.id) ?? 0) + cost);
  }

  const budgetBySpaceId = new Map(
    spaceBudgets.map((budget) => [budget.scopeId, budget] as const)
  );

  const spaceRows: AiProviderAllocationRow[] = [];
  for (const space of spaces) {
    const budget = budgetBySpaceId.get(space.id);
    const spend = spendBySpace.get(space.id);
    if (spend == null && !budget) continue;
    spaceRows.push({
      id: space.id,
      label: space.name,
      spendUsd: spend ?? null,
      limitAmount: budget?.limitAmount ?? null,
      kind: "space",
      budgetId: budget?.id,
    });
  }
  spaceRows.sort(
    (a, b) =>
      (b.spendUsd ?? 0) - (a.spendUsd ?? 0) || a.label.localeCompare(b.label)
  );

  const aggregateRows: AiProviderAllocationRow[] = [];
  if (personalSpend > 0) {
    aggregateRows.push({
      id: "personal",
      label: "Personal",
      spendUsd: personalSpend,
      limitAmount: null,
      kind: "personal",
    });
  }
  const emittedSpaceSpend = spaceRows.reduce(
    (sum, row) => sum + (row.spendUsd ?? 0),
    0
  );
  const remainder = (totalSpendUsd ?? 0) - emittedSpaceSpend - personalSpend;
  if (remainder > 0.005) {
    aggregateRows.push({
      id: "unattributed",
      label: "Unattributed",
      spendUsd: remainder,
      limitAmount: null,
      kind: "unattributed",
    });
  }

  return [...rows, ...spaceRows, ...aggregateRows];
}
